import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { PageHeader } from "../components/ui/page-header";
import { ExpenseForm, type ExpenseFormSubmit } from "../components/ExpenseForm";
import { useExpense, useGroup, useUpdateExpense } from "../api/hooks";
import type { Expense, Group, UpdateExpenseRequest } from "../api/types";
import { itemsEqual, paymentsEqual } from "../lib/expenseDiff";

export function EditExpensePage(): ReactNode {
  const { groupId, expenseId } = useParams<{
    groupId: string;
    expenseId: string;
  }>();
  const groupQ = useGroup(groupId);
  const expenseQ = useExpense(groupId, expenseId);

  if (groupQ.isLoading || expenseQ.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-stack-lg">
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!groupQ.data || !expenseQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Expense not found.
        </CardContent>
      </Card>
    );
  }
  return <EditExpense group={groupQ.data} expense={expenseQ.data} />;
}

function EditExpense({
  group,
  expense,
}: {
  group: Group;
  expense: Expense;
}): ReactNode {
  const navigate = useNavigate();
  const updateExpense = useUpdateExpense(group.id);
  const detailPath = `/groups/${group.id}/expenses/${expense.id}`;
  // Re-mount the form whenever the expense or participant set changes so
  // a background refetch can't leave the editor seeded from a stale
  // snapshot. `expense.updatedAt` covers the expense; participant ids
  // cover late-joiners / removals on the group.
  const formKey = `${expense.updatedAt}|${group.participants
    .map((p) => p.id)
    .sort()
    .join("|")}`;

  async function onSubmit(values: ExpenseFormSubmit): Promise<void> {
    const body = buildUpdateBody(expense, values);
    // `version` is always present (concurrency token); a body with nothing
    // but `version` means the user changed nothing, so skip the write.
    if (!hasExpenseChanges(body)) {
      navigate(detailPath);
      return;
    }
    await updateExpense.mutateAsync({ expenseId: expense.id, body });
    navigate(detailPath);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-stack-lg">
      <PageHeader
        back={{ label: "Back", onClick: () => navigate(detailPath) }}
        title="Edit expense"
        description="Change the description, items, splits, or who paid. Items kept from the previous version preserve their identity (and their comments) when the change is saved."
      />
      <ExpenseForm
        key={formKey}
        group={group}
        expense={expense}
        submitLabel="Save changes"
        pendingLabel="Saving…"
        submitting={updateExpense.isPending}
        serverError={updateExpense.error ? "Couldn't save changes." : null}
        onCancel={() => navigate(detailPath)}
        onSubmit={onSubmit}
      />
    </div>
  );
}

/**
 * Send only the sections the user actually changed. The server's
 * `validateExpense` re-runs participant/charge/split checks on whatever
 * the request includes; submitting the full body for a description-only
 * edit can fail with 422 when an unrelated field has gone stale (e.g.
 * a participant referenced by a split was removed since page-load).
 */
function buildUpdateBody(
  expense: Expense,
  values: ExpenseFormSubmit,
): UpdateExpenseRequest {
  // `version` is the optimistic-concurrency token: it's required on every
  // update so the server can reject (409) a write made against a stale
  // snapshot rather than silently overwriting a concurrent edit.
  const body: UpdateExpenseRequest = { version: expense.version };
  if (values.label !== expense.label) body.label = values.label;
  if (!paymentsEqual(values.payments, expense.payments)) {
    body.payments = values.payments;
  }
  if (!itemsEqual(values.items, expense.items)) {
    body.items = values.items;
  }
  return body;
}

/**
 * True when the update body carries at least one changed field beyond the
 * always-present `version` concurrency token.
 */
function hasExpenseChanges(body: UpdateExpenseRequest): boolean {
  return Object.keys(body).some((key) => key !== "version");
}
